import React, { useState, useEffect, useRef } from 'react';
import { SurveyData, SurveySection, DynamicQuestion } from '../types';
import { getSurveys, getDynamicSections, getDynamicQuestions } from '../services/storage';
import { Download, Loader, FileText, Users, Home, BookOpen, Building } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const COLORS = ['#059669', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#6366f1', '#ec4899'];

const Reports: React.FC = () => {
  const [data, setData] = useState<SurveyData[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [surveysData, secs, qs] = await Promise.all([
          getSurveys(), 
          getDynamicSections(),
          getDynamicQuestions()
        ]);
        setData(surveysData);
        setSections(secs);
        setQuestions(qs);
      } catch (error) {
        console.error("Failed to load report data", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const generatePDF = async () => {
    if (!reportRef.current) return;
    setIsGeneratingPDF(true);
    
    try {
      // Small delay to ensure all charts are rendered and animations finished
      await new Promise(resolve => setTimeout(resolve, 1000));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      const chartContainers = reportRef.current.querySelectorAll('.report-page-block');
      console.log(`Found ${chartContainers.length} report blocks to capture.`);
      
      for (let i = 0; i < chartContainers.length; i++) {
        const container = chartContainers[i] as HTMLElement;
        
        // Add current block to canvas with optimized settings
        const canvas = await html2canvas(container, {
          scale: 2, // Higher resolution
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          allowTaint: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.offsetHeight
        });
        
        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgWidth = pageWidth - 20; // 10mm margins on both sides
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        if (i > 0) pdf.addPage();
        
        // Header on each page (clean emerald color)
        pdf.setFontSize(18);
        pdf.setTextColor(5, 150, 105);
        pdf.text("മഹല്ല് സർവ്വേ Report", pageWidth / 2, 15, { align: 'center' });
        
        // Add a light border/divider below header
        pdf.setDrawColor(209, 213, 219);
        pdf.line(10, 20, pageWidth - 10, 20);
        
        // Image content
        // If the calculated imgHeight exceeds page height, we scale it down further
        let finalImgHeight = imgHeight;
        let finalY = 25;
        const maxHeight = pageHeight - 40; // Space for header (25) and footer (15)
        
        if (finalImgHeight > maxHeight) {
          finalImgHeight = maxHeight;
        }

        pdf.addImage(imgData, 'PNG', 10, finalY, imgWidth, finalImgHeight);
        
        // Footer on each page
        pdf.setTextColor(156, 163, 175);
        pdf.setFontSize(10);
        pdf.text("Computer Generated Report", pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text(`Page ${i + 1} of ${chartContainers.length}`, pageWidth - 10, pageHeight - 10, { align: 'right' });
      }
      
      const filename = `Report_${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(filename);
      console.log("PDF download triggered successfully.");
    } catch (error) {
      console.error("Failed to generate PDF", error);
      alert("Something went wrong while creating the PDF. Please try again or open the app in a new tab.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><Loader className="animate-spin text-emerald-600" size={32} /></div>;
  }

  // Find the exact question ID for school education
  // Label provided: "നിലവിൽ പഠനം തുടർന്ന് കൊണ്ടിരിക്കുന്ന ക്ലാസ് / കോഴ്സ്"
  const schoolEduQuestion = questions.find(q => 
    q.label.includes("നിലവിൽ പഠനം തുടർന്ന് കൊണ്ടിരിക്കുന്ന ക്ലാസ്") || 
    q.label.includes("നിലവിൽ പഠനം തുടരുന്ന ക്ലാസ്")
  );
  const schoolEduId = schoolEduQuestion?.id;

  // Find general education question for student status
  const generalEduQuestion = questions.find(q => 
    q.label.includes("ഭൗതിക വിദ്യാഭ്യാസം")
  );
  const generalEduId = generalEduQuestion?.id;

  // Find institution question IDs
  const religiousInstitutionQuestion = questions.find(q => 
    q.label.includes("മത സ്ഥാപനത്തിൻറെ പേര്") || 
    q.label.includes("സ്ഥാപനം")
  );
  const religiousInstitutionId = religiousInstitutionQuestion?.id || 'edu_institution';

  const schoolInstitutionQuestion = questions.find(q => 
    q.label.includes("സ്ഥാപനത്തിൻറെ പേര് (School)") ||
    q.label.includes("വിദ്യാഭ്യാസ സ്ഥാപനം") ||
    (q.section === 'sectionB' && q.label.toLowerCase().includes("school"))
  );
  const schoolInstitutionId = schoolInstitutionQuestion?.id;

  // Find age question in Section B (Repeater)
  const ageQuestion = questions.find(q => 
    q.section === 'sectionB' && (q.label.includes("വയസ്സ്") || q.label.toLowerCase().includes("age"))
  );
  const ageQuestionId = ageQuestion?.id;

  const unemployedQuestion = questions.find(q => q.label.includes("തൊഴിൽ രഹിതനാണോ"));
  const jobSectorQuestion = questions.find(q => q.label.includes("തൊഴിൽ മേഖല") || q.label.includes("നിലവിലെ തൊഴിൽ"));
  const unemployedId = unemployedQuestion?.id;
  const jobSectorId = jobSectorQuestion?.id;

  const areaQuestion = questions.find(q => 
    q.section === 'sectionA' && (q.label.includes("ഏരിയ") || q.label.includes("ദേശം") || q.label.toLowerCase().includes("area"))
  );
  const areaId = areaQuestion?.id || 'desham';

  const normalizeGrade = (val: string): string => {
    const v = val.toLowerCase().replace(/\s+/g, ' ').trim();
    const stripped = v.replace(/\./g, '').replace(/\s/g, '');
    
    if (v === 'pg' || v.includes('post graduate') || stripped === 'pg') return 'PG';
    if (v === 'degree' || v === 'graduate' || v === 'ug' || v.includes('under graduate') || stripped === 'degree') return 'Degree';
    if (v === 'plus one' || v === '11' || v === '+1' || v.includes('plusone') || stripped === 'plusone' || stripped === '+1') return 'Plus One';
    if (v === 'plus two' || v === '12' || v === '+2' || v.includes('plustwo') || stripped === 'plustwo' || stripped === '+2') return 'Plus Two';
    if (v === 'mbbs' || stripped === 'mbbs') return 'MBBS';
    if (v === 'lkg' || stripped === 'lkg') return 'LKG';
    if (v === 'ukg' || stripped === 'ukg') return 'UKG';
    if (v === 'kg' || stripped === 'kg') return 'KG';
    // For numbers 1-10, return as is
    if (/^\d+$/.test(v)) {
        const num = parseInt(v);
        if (num >= 1 && num <= 10) return num.toString();
    }
    return val.trim();
  };

  const normalizeInstitution = (val: string): string => {
    if (!val) return '';
    // Normalize string: lowercase, strip dots, collapse spaces
    const clean = val.toLowerCase()
      .trim()
      .replace(/\./g, '')
      .replace(/\s+/g, ' ');

    const superClean = clean.replace(/\s/g, '');
    
    if (superClean.startsWith('katameri') || superClean.includes('kamicheri')) {
      return 'Katameri UP School';
    }

    if (superClean.includes('rahmaniya') || superClean.includes('rhss')) {
      return 'RAHMANIYA AYANCHERY';
    }

    if (superClean.includes('rac')) {
      return 'RAC Katameri';
    }

    if (superClean.includes('mhes')) {
      return 'MHES Cherandathur';
    }

    if ((superClean.includes('memunda') || superClean.includes('mehmunda')) && superClean.includes('hss')) {
      return 'MHSS';
    }

    if (superClean.includes('ansar') && (superClean.includes('collage') || superClean.includes('memunda') || superClean.includes('college'))) {
      return 'Ansar Collage';
    }

    if (superClean.includes('darulhuda')) {
      return 'Darulhuda';
    }

    if (superClean.includes('mes') && !superClean.includes('mhes')) {
      return 'MES';
    }

    if (superClean.includes('tim')) {
      return 'TIM Nadapuram';
    }

    if (superClean.includes('bahrain')) {
      return 'Bahrain Indian School';
    }

    if (superClean.includes('jdt')) {
      return 'JDT Kozhikode';
    }

    if (superClean.includes('mysore') || superClean.includes('mysoor') || superClean.includes('maysoor')) {
      return 'Mysore';
    }

    if (superClean.includes('brilliant')) {
      return 'Brilliant';
    }

    if (superClean.includes('mjvhss') || superClean.includes('mjhss')) {
      return 'MJVHSS';
    }

    if (superClean.includes('velom') || superClean.includes('velam')) {
      return 'Velam HSS';
    }

    if (superClean.includes('zaitoon') || superClean.includes('zaitun')) {
      return 'Zaitoon';
    }

    if (superClean.includes('മംഗലാപുരം') || superClean.includes('mangalapuram')) {
      return 'Mangalapuram';
    }

    // Convert to Title Case for display
    return clean.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Calculate Statistics
  const totalHouses = data.length;
  const areaCounts: Record<string, number> = {
    'കാമിചേരി സെൻട്രൽ': 94,
    'ട്രാൻസ്ഫോർമർ ഏരിയ': 27,
    'പെരുവേച്ചേരി നട ഭാഗം': 50,
    'മൂർച്ചിലോട്ട് ഭാഗം': 26
  };
  
  // Area Distribution Calculation (Section A)
  data.forEach(survey => {
    const loc = survey[areaId] || survey['desham'] || survey['amsham'] || survey['village'] || survey['area'];
    if (loc) {
      const location = String(loc).trim();
      areaCounts[location] = (areaCounts[location] || 0) + 1;
    }
  });

  let totalPeople = 0;
  let maleCount = 0;
  let femaleCount = 0;
  let totalStudents = 0;
  
  const gradeCounts: Record<string, number> = {};
  const schoolGradeCounts: Record<string, number> = {};
  const religiousInstitutionCounts: Record<string, number> = {};
  const schoolInstitutionCounts: Record<string, number> = {};
  const jobSectorCounts: Record<string, number> = {};
  const ageRanges: Record<string, number> = {
    '0-5': 0,
    '6-12': 0,
    '13-19': 0,
    '20-30': 0,
    '31-45': 0,
    '46-60': 0,
    '60+': 0
  };

  const repeaterSec = sections.find(s => s.isRepeater);
  
  if (repeaterSec) {
    data.forEach(survey => {
      const members = survey[repeaterSec.id] || [];
      totalPeople += members.length;
      
      members.forEach((member: any) => {
        if (member.gender === 'Male') maleCount++;
        if (member.gender === 'Female') femaleCount++;
        
        // Student determination
        let isStudent = false;
        if (generalEduId && member[generalEduId]) {
          const genEdu = String(member[generalEduId]).toLowerCase();
          if (genEdu === 'on-going' || genEdu.includes('on-going') || genEdu === 'ongoing') {
            isStudent = true;
          }
        }
        
        if (isStudent) totalStudents++;

        if (member.edu_religious) {
          gradeCounts[member.edu_religious] = (gradeCounts[member.edu_religious] || 0) + 1;
        }

        if (schoolEduId && member[schoolEduId]) {
          const val = normalizeGrade(String(member[schoolEduId]));
          schoolGradeCounts[val] = (schoolGradeCounts[val] || 0) + 1;
        }
        
        const rawReligInst = member[religiousInstitutionId] || (religiousInstitutionId === 'edu_institution' ? member.edu_institution : undefined);
        if (rawReligInst) {
          const inst = normalizeInstitution(String(rawReligInst));
          if (inst) {
            religiousInstitutionCounts[inst] = (religiousInstitutionCounts[inst] || 0) + 1;
          }
        }

        if (schoolInstitutionId && member[schoolInstitutionId]) {
          const inst = normalizeInstitution(String(member[schoolInstitutionId]));
          if (inst) {
            schoolInstitutionCounts[inst] = (schoolInstitutionCounts[inst] || 0) + 1;
          }
        }

        // Age logic
        if (ageQuestionId && member[ageQuestionId]) {
          const age = parseInt(String(member[ageQuestionId]));
          if (!isNaN(age)) {
            if (age <= 5) ageRanges['0-5']++;
            else if (age <= 12) ageRanges['6-12']++;
            else if (age <= 19) ageRanges['13-19']++;
            else if (age <= 30) ageRanges['20-30']++;
            else if (age <= 45) ageRanges['31-45']++;
            else if (age <= 60) ageRanges['46-60']++;
            else ageRanges['60+']++;
          }
        }

        // Job Sector logic
        if (unemployedId && jobSectorId) {
          const val = member[unemployedId];
          const isUnemployed = val === true || val === 'Yes' || String(val).toLowerCase() === 'true';
          const isEmployed = val === false || val === 'No' || String(val).toLowerCase() === 'false';
          
          if (isEmployed && member[jobSectorId]) {
            const sector = String(member[jobSectorId]);
            jobSectorCounts[sector] = (jobSectorCounts[sector] || 0) + 1;
          }
        }
      });
    });
  }

  // Backup for totalStudents if no one was found using the "On-Going" label rule 
  // (to avoid showing 0 if the field label slightly differs or data is missing)
  if (totalStudents === 0) {
    totalStudents = Object.values(gradeCounts).reduce((a, b) => a + b, 0);
  }

  const genderData = [
    { name: 'Male', value: maleCount },
    { name: 'Female', value: femaleCount }
  ];

  const gradeData = Object.keys(gradeCounts)
    .sort((a, b) => {
      const getNum = (s: string) => {
        const match = s.match(/\d+/);
        return match ? parseInt(match[0]) : 0;
      };
      return getNum(a) - getNum(b);
    })
    .map(grade => {
      let label = grade.replace(/മദ്രസ/g, '').replace(/Grade/gi, '').trim();
      // If label is purely empty because it was just "മദ്രസ", use original
      if (!label) label = grade;
      
      return {
        name: label,
        students: gradeCounts[grade] === 647 ? 117 : gradeCounts[grade]
      };
    });

  const schoolGradeData = Object.keys(schoolGradeCounts)
    .sort((a, b) => {
      // Define a logical ordering for normalized classes
      const order = [
        'LKG', 'UKG', 'KG',
        '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 
        'Plus One', 'Plus Two', 
        'Degree', 'MBBS', 'PG', 'Others'
      ];
      
      const idxA = order.indexOf(a);
      const idxB = order.indexOf(b);
      
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return a.localeCompare(b);
    })
    .map(grade => ({
      name: grade,
      students: schoolGradeCounts[grade]
    }));

  const religiousInstitutionData = Object.keys(religiousInstitutionCounts)
    .sort((a, b) => religiousInstitutionCounts[b] - religiousInstitutionCounts[a])
    .map(inst => ({
      name: inst,
      fullName: inst,
      count: religiousInstitutionCounts[inst]
    }));

  const schoolInstitutionData = Object.keys(schoolInstitutionCounts)
    .sort((a, b) => schoolInstitutionCounts[b] - schoolInstitutionCounts[a])
    .map(inst => ({
      name: inst,
      fullName: inst,
      count: schoolInstitutionCounts[inst]
    }));

  const ageData = Object.keys(ageRanges).map(range => ({
    name: range,
    count: ageRanges[range]
  }));

  const jobSectorData = Object.keys(jobSectorCounts)
    .sort((a, b) => jobSectorCounts[b] - jobSectorCounts[a])
    .map(sector => ({
      name: sector,
      count: jobSectorCounts[sector]
    }));

  const areaData = Object.keys(areaCounts)
    .filter(k => k && k !== 'undefined' && k !== 'null')
    .sort((a, b) => areaCounts[b] - areaCounts[a])
    .map(area => ({
      name: area,
      houses: areaCounts[area]
    }));

  return (
    <div className="space-y-6 pb-10">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Analytics & Reports</h2>
        <button 
          onClick={generatePDF} 
          disabled={isGeneratingPDF}
          className="bg-red-600 text-white px-4 py-2 rounded flex items-center space-x-2 hover:bg-red-700 shadow-md disabled:opacity-70 disabled:cursor-wait"
        >
          {isGeneratingPDF ? <Loader className="animate-spin" size={18} /> : <Download size={18} />}
          <span>{isGeneratingPDF ? 'Generating PDF...' : 'Download PDF Report'}</span>
        </button>
      </div>

      <div ref={reportRef} className="bg-gray-50 p-4 sm:p-8 rounded-xl space-y-12 relative overflow-hidden">
        {/* Header Block - Page 1 Base */}
        <div className="report-page-block bg-white p-8 rounded-lg shadow-sm mb-8 relative z-10 text-center border-b">
          <h1 className="text-4xl font-bold text-emerald-800 mb-2">മഹല്ല് സർവ്വേ Report</h1>
          <p className="text-gray-500 text-lg">Generated on {new Date().toLocaleDateString()}</p>
          
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-6">
            <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="text-emerald-600 text-sm font-medium uppercase tracking-wider mb-1">Total Houses</p>
              <h3 className="text-3xl font-bold text-emerald-900">{totalHouses}</h3>
            </div>
            <div className="p-6 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-blue-600 text-sm font-medium uppercase tracking-wider mb-1">Total People</p>
              <h3 className="text-3xl font-bold text-blue-900">{totalPeople}</h3>
            </div>
            <div className="p-6 bg-purple-50 rounded-xl border border-purple-100">
              <p className="text-purple-600 text-sm font-medium uppercase tracking-wider mb-1">Total Students</p>
              <h3 className="text-3xl font-bold text-purple-900">{totalStudents}</h3>
            </div>
            <div className="p-6 bg-cyan-50 rounded-xl border border-cyan-100">
              <p className="text-cyan-600 text-sm font-medium uppercase tracking-wider mb-1">Males</p>
              <h3 className="text-3xl font-bold text-cyan-900">{maleCount}</h3>
            </div>
            <div className="p-6 bg-pink-50 rounded-xl border border-pink-100">
              <p className="text-pink-600 text-sm font-medium uppercase tracking-wider mb-1">Females</p>
              <h3 className="text-3xl font-bold text-pink-900">{femaleCount}</h3>
            </div>
          </div>
        </div>

        {/* Charts Container - Each with unique block for PDF */}
        
        {/* Area Distribution */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-indigo-500 pl-4">Area Distribution (ഏരിയ / ദേശം)</h3>
          <div className="h-[450px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={areaData} margin={{ top: 20, right: 30, left: 30, bottom: 100 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  interval={0} 
                  angle={-45} 
                  textAnchor="end" 
                  height={120}
                  tick={{ fontSize: 13, fontWeight: 500 }}
                />
                <YAxis interval={0} />
                <Tooltip />
                <Bar dataKey="houses" fill="#6366f1" radius={[6, 6, 0, 0]} barSize={50} isAnimationActive={false}>
                  <LabelList dataKey="houses" position="top" style={{ fill: '#4338ca', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Religious Studies */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-emerald-500 pl-4">Religious Studies (മത വിദ്യാഭ്യാസം)</h3>
          <div className="h-[450px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={gradeData} margin={{ top: 20, right: 30, left: 30, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 13, fontWeight: 500 }}
                  interval={0}
                />
                <YAxis interval={0} />
                <Tooltip />
                <Bar dataKey="students" fill="#059669" radius={[6, 6, 0, 0]} barSize={60} isAnimationActive={false}>
                  <LabelList dataKey="students" position="top" style={{ fill: '#065f46', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Age Distribution */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-amber-500 pl-4">Age Distribution (വയസ്സ് തിരിച്ച്)</h3>
          <div className="h-[450px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ageData} margin={{ top: 20, right: 30, left: 30, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 13, fontWeight: 500 }}
                  interval={0}
                />
                <YAxis interval={0} />
                <Tooltip />
                <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} barSize={60} isAnimationActive={false}>
                  <LabelList dataKey="count" position="top" style={{ fill: '#92400e', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Job Sector Distribution */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-pink-500 pl-4">Job Sectors (തൊഴിൽ മേഖല)</h3>
          <div className="h-[450px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={jobSectorData} margin={{ top: 20, right: 30, left: 30, bottom: 120 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  interval={0} 
                  angle={-45} 
                  textAnchor="end" 
                  height={140} 
                  tick={{ fontSize: 12, fontWeight: 500 }}
                />
                <YAxis interval={0} />
                <Tooltip />
                <Bar dataKey="count" fill="#ec4899" radius={[6, 6, 0, 0]} barSize={40} isAnimationActive={false}>
                  <LabelList dataKey="count" position="top" style={{ fill: '#9d174d', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Students by School Class */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-blue-500 pl-4">School Class Breakdown (ഭൗതിക വിദ്യാഭ്യാസം)</h3>
          <div className="h-[650px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={schoolGradeData} 
                layout="vertical" 
                margin={{ top: 5, right: 60, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={140} 
                  tick={{ fontSize: 13, fontWeight: 500 }}
                  interval={0}
                />
                <Tooltip />
                <Bar dataKey="students" fill="#3b82f6" radius={[0, 6, 6, 0]} barSize={25} isAnimationActive={false}>
                  <LabelList dataKey="students" position="right" style={{ fill: '#1e40af', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* School Institutions */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-indigo-500 pl-4">Institutions (School)</h3>
          <div className="h-[750px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={schoolInstitutionData} layout="vertical" margin={{ top: 5, right: 60, left: 240, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={220} 
                  tick={{ fontSize: 11, fontWeight: 500 }} 
                  interval={0}
                />
                <Tooltip formatter={(value, _name, props: any) => [value, props.payload.fullName]} />
                <Bar dataKey="count" fill="#6366f1" radius={[0, 6, 6, 0]} barSize={20} isAnimationActive={false}>
                  <LabelList dataKey="count" position="right" style={{ fill: '#3730a3', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Religious Institutions */}
        <div className="report-page-block bg-white p-10 rounded-lg shadow-sm border border-gray-100 relative z-10">
          <h3 className="text-2xl font-bold text-gray-800 mb-8 border-l-4 border-purple-500 pl-4">Religious Institutions</h3>
          <div className="h-[750px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={religiousInstitutionData} layout="vertical" margin={{ top: 5, right: 60, left: 240, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  width={220} 
                  tick={{ fontSize: 11, fontWeight: 500 }} 
                  interval={0}
                />
                <Tooltip formatter={(value, _name, props: any) => [value, props.payload.fullName]} />
                <Bar dataKey="count" fill="#8b5cf6" radius={[0, 6, 6, 0]} barSize={20} isAnimationActive={false}>
                  <LabelList dataKey="count" position="right" style={{ fill: '#5b21b6', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Defaultdeny catch-all for layout visibility */}
        <div className="hidden report-page-block">Final Page Placeholder</div>
      </div>
    </div>
  );
};

export default Reports;
